import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Badge,
  Dropdown,
  Option,
  Textarea,
  Field,
  Divider,
  Tooltip,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  DismissRegular,
  MailRegular,
  PersonRegular,
  ClockRegular,
  CheckmarkCircleRegular,
  DocumentBulletListRegular,
  LinkRegular,
  LinkDismissRegular,
  WarningRegular,
  QuestionCircleRegular,
  MoneyRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import {
  getIrsNotice,
  getTaxReturns,
  updateIrsNotice,
  linkNoticeAmendment,
  unlinkNoticeAmendment,
} from '../services/api';
import {
  type IrsNoticeStatus,
  type NoticeAmendmentView,
  type SystemUser,
  type TaxReturn,
  irsNoticeStatusDisplayNames,
  irsNoticeStatusOptions,
  statusDisplayNames,
} from '../types';
import {
  daysUntilDue,
  dueBucket,
  formatDueLabel,
  formatNoticeDate,
} from '../utils/irsNoticeDue';
import { resolveBlockReason } from '../utils/irsNoticeResolve';

/**
 * The one screen.
 *
 * The spec's success criterion is not "a component exists" — it is that a staff member who picks up
 * the phone to a client holding a letter answers **without hanging up and without opening a second
 * system**. Everything above the first Divider is that answer: what letter, when it arrived, when a
 * response is due, who owns it, where we are, and did we already amend.
 *
 * Two things this panel does deliberately:
 *
 * 1. **It refetches by (id, clientId) instead of taking the list row.** The detail response resolves
 *    each linked amendment's status LIVE from the linked return at request time; the row in the queue
 *    does not have that and must not pretend to. `clientId` is the partition key — the API 400s
 *    without it.
 * 2. **A blocked Resolve shows its reason.** Never a bare disabled button: a greyed control with no
 *    explanation tells the reader nothing about what to do next. The reason is computed locally so it
 *    appears without a round trip, and the server still gets the final word — if it refuses anyway,
 *    its message is surfaced rather than swallowed.
 */

interface NoticeDetailPanelProps {
  noticeId: string;
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  systemUsers?: SystemUser[];
  onUpdate?: () => void;
}

const dueBucketColors: Record<string, string> = {
  overdue: '#EF4444',
  due_soon: '#F59E0B',
  later: '#10B981',
  none: '#9CA3AF',
};

/** A label/value row in the answer block. */
function Fact({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '6px 0' }}>
      {icon && <div style={{ color: 'var(--tl-text-secondary)', marginTop: '2px' }}>{icon}</div>}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>{label}</div>
        <div style={{ fontWeight: 500 }}>{children}</div>
      </div>
    </div>
  );
}

/**
 * One linked amendment, rendered as THREE distinct states rather than two.
 *
 * `isComplete` is `true` / `false` / unknown, and unknown is not a styling nicety — it is the
 * difference between "the fix has not landed" and "we could not read whether it landed". The second
 * is what blocks a resolution on evidence nobody has, so it gets its own icon and its own words.
 */
function AmendmentRow({
  amendment,
  onUnlink,
  unlinking,
}: {
  amendment: NoticeAmendmentView;
  onUnlink: () => void;
  unlinking: boolean;
}) {
  const known = amendment.isComplete != null;
  const complete = amendment.isComplete === true;

  const icon = !known ? (
    <QuestionCircleRegular style={{ color: '#9CA3AF' }} />
  ) : complete ? (
    <CheckmarkCircleRegular style={{ color: '#10B981' }} />
  ) : (
    <WarningRegular style={{ color: '#F59E0B' }} />
  );

  const statusText = !known
    ? 'Status unknown — this return could not be read'
    : amendment.status
      ? statusDisplayNames[amendment.status]
      : 'No status recorded';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '10px 12px',
        border: '1px solid var(--tl-border, #e0e0e0)',
        borderRadius: '4px',
        marginBottom: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        {icon}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{amendment.taxYear} amendment</div>
          <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>{statusText}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        {/* Only rendered when the return is readable — a link to a return we could not load would
            dead-end the reader mid phone call. */}
        {known && (
          <Tooltip content="Open the amendment return" relationship="label">
            <RouterLink to={`/tax-returns/${amendment.taxReturnId}`}>
              <Button appearance="subtle" size="small" icon={<DocumentBulletListRegular />} />
            </RouterLink>
          </Tooltip>
        )}
        <Tooltip content="Unlink this amendment" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<LinkDismissRegular />}
            onClick={onUnlink}
            disabled={unlinking}
          />
        </Tooltip>
      </div>
    </div>
  );
}

export default function NoticeDetailPanel({
  noticeId,
  clientId,
  open,
  onOpenChange,
  systemUsers,
  onUpdate,
}: NoticeDetailPanelProps) {
  const queryClient = useQueryClient();
  const [pendingStatus, setPendingStatus] = useState<IrsNoticeStatus | undefined>();
  const [statusNotes, setStatusNotes] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [amendmentToLink, setAmendmentToLink] = useState<string | undefined>();

  const now = new Date();

  const {
    data: detail,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['irsNotice', noticeId, clientId],
    queryFn: () => getIrsNotice(noticeId, clientId),
    enabled: open,
  });

  const notice = detail?.notice;

  // The client's amendment returns, for the link picker. Fetched only while the panel is open and a
  // notice is loaded — this is a secondary query and should not run for a closed drawer.
  const { data: clientReturns } = useQuery<TaxReturn[]>({
    queryKey: ['taxReturns', { clientId }],
    queryFn: () => getTaxReturns({ clientId }),
    enabled: open && !!notice,
  });

  useEffect(() => {
    setPendingStatus(undefined);
    setStatusNotes('');
    setResolutionNotes('');
    setAmendmentToLink(undefined);
  }, [noticeId, clientId]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['irsNotice', noticeId, clientId] });
    queryClient.invalidateQueries({ queryKey: ['irsNotices'] });
    queryClient.invalidateQueries({ queryKey: ['irsNoticeStats'] });
    onUpdate?.();
  };

  const updateMutation = useMutation({
    mutationFn: (data: {
      status?: IrsNoticeStatus;
      statusNotes?: string;
      assignedTo?: string;
      assignedToName?: string;
      resolutionNotes?: string;
    }) => updateIrsNotice(noticeId, { clientId, ...data }),
    onSuccess: () => {
      setPendingStatus(undefined);
      setStatusNotes('');
      invalidate();
    },
  });

  const linkMutation = useMutation({
    mutationFn: (taxReturnId: string) => {
      const target = clientReturns?.find((r) => r.id === taxReturnId);
      return linkNoticeAmendment(noticeId, {
        clientId,
        taxReturnId,
        // Lets the server point-read the return instead of fanning out. Omitted when the parent has
        // no appointmentId rather than sent as an empty string, which would name a nonexistent target.
        appointmentId: target?.appointmentId || undefined,
      });
    },
    onSuccess: () => {
      setAmendmentToLink(undefined);
      invalidate();
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (taxReturnId: string) => unlinkNoticeAmendment(noticeId, taxReturnId, clientId),
    onSuccess: invalidate,
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Spinner label="Loading notice..." />
        </div>
      );
    }

    if (error || !detail || !notice) {
      return (
        <MessageBar intent="error">
          <MessageBarBody>
            {error instanceof Error ? error.message : 'This notice could not be loaded.'}
          </MessageBarBody>
        </MessageBar>
      );
    }

    const amendments = detail.amendments ?? [];
    const blockReason = resolveBlockReason(amendments);
    const alreadyResolved = notice.status === 'resolved';
    const days = daysUntilDue(notice.responseDueDate, now);
    const bucket = dueBucket(days);

    // Candidates for linking: the same client's amendments, minus what is already linked. This
    // mirrors server guards G3 (must be an amendment) and G4 (must be the same client) in the
    // AFFORDANCE, so the invalid link is not offered rather than being offered and refused.
    // `isAmendment` is optional on the wire, so this tests for true rather than truthiness.
    const linkedIds = new Set(notice.amendmentLinks.map((l) => l.taxReturnId));
    const linkCandidates = (clientReturns ?? []).filter(
      (r) => r.isAmendment === true && !linkedIds.has(r.id)
    );

    const mutationError =
      updateMutation.error || linkMutation.error || unlinkMutation.error || null;

    return (
      <>
        {/* ── The answer to the phone call ─────────────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '4px 20px',
          }}
        >
          <Fact icon={<MailRegular />} label="Notice">
            {notice.noticeNumber || 'Unnumbered'}
            {notice.authority !== 'irs' && (
              <span
                style={{
                  marginLeft: '6px',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  color: 'var(--tl-text-secondary)',
                }}
              >
                {notice.authority}
              </span>
            )}
          </Fact>

          <Fact icon={<PersonRegular />} label="Client">
            <div>{notice.clientName || '-'}</div>
            {/* On a business return the client is the company and the contact is a person there —
                different guids, measured 40/40. The staff member needs the person's name to ask for
                them by name, and the company name to know which entity the letter concerns. */}
            {notice.contactName && notice.contactName !== notice.clientName && (
              <div style={{ fontSize: '12px', fontWeight: 400, color: 'var(--tl-text-secondary)' }}>
                Contact: {notice.contactName}
              </div>
            )}
          </Fact>

          <Fact icon={<ClockRegular />} label="Received">
            {/* UTC day. A local render would show every US reader the day before, and would
                disagree with the UTC-based due-day count directly beside it. */}
            {formatNoticeDate(notice.noticeReceivedDate)}
          </Fact>

          <Fact icon={<ClockRegular />} label="Response due">
            <span style={{ color: bucket === 'overdue' ? dueBucketColors.overdue : undefined }}>
              {formatDueLabel(notice.responseDueDate, now)}
            </span>
            {notice.responseDueDate && days !== null && (
              <div style={{ fontSize: '12px', fontWeight: 400, color: 'var(--tl-text-secondary)' }}>
                {formatNoticeDate(notice.responseDueDate)}
              </div>
            )}
          </Fact>

          <Fact icon={<PersonRegular />} label="Owner">
            {notice.assignedToName || (
              <span style={{ fontStyle: 'italic', color: 'var(--tl-text-secondary)' }}>
                Unassigned
              </span>
            )}
          </Fact>

          <Fact label="Tax year">{notice.taxYear}</Fact>

          {notice.amountAtStake != null && (
            <Fact icon={<MoneyRegular />} label="Amount at stake">
              {notice.amountAtStake.toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
              })}
            </Fact>
          )}

          <Fact icon={<DocumentBulletListRegular />} label="Return">
            <RouterLink to={`/tax-returns/${notice.taxReturnId}`}>Open tax return</RouterLink>
          </Fact>
        </div>

        {notice.summary && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>What it says</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{notice.summary}</div>
          </div>
        )}

        <Divider style={{ margin: '20px 0' }} />

        {mutationError && (
          <MessageBar intent="error" style={{ marginBottom: '12px' }}>
            <MessageBarBody>
              {/* The server's refusal, verbatim. If its rules and the panel's local mirror ever
                  disagree, the server is authoritative and the reader sees why. */}
              {mutationError instanceof Error ? mutationError.message : 'That change was refused.'}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* ── Amendments ───────────────────────────────────────────────────────────────────── */}
        <h4 style={{ margin: '0 0 8px' }}>Amendments</h4>

        {amendments.length === 0 ? (
          <div style={{ color: 'var(--tl-text-secondary)', marginBottom: '12px' }}>
            No amendment linked. Not every notice needs one.
          </div>
        ) : (
          amendments.map((amendment) => (
            <AmendmentRow
              key={amendment.taxReturnId}
              amendment={amendment}
              unlinking={unlinkMutation.isPending}
              onUnlink={() => unlinkMutation.mutate(amendment.taxReturnId)}
            />
          ))
        )}

        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '20px' }}>
          <Field label="Link an amendment" style={{ flex: 1 }}>
            <Dropdown
              placeholder={
                linkCandidates.length > 0
                  ? 'Select an amendment return'
                  : 'No unlinked amendment returns for this client'
              }
              disabled={linkCandidates.length === 0}
              value={
                amendmentToLink
                  ? `${linkCandidates.find((r) => r.id === amendmentToLink)?.taxYear ?? ''} amendment`
                  : ''
              }
              onOptionSelect={(_, data) => setAmendmentToLink(data.optionValue)}
            >
              {linkCandidates.map((r) => (
                <Option key={r.id} text={`${r.taxYear} amendment`} value={r.id}>
                  {`${r.taxYear} amendment`}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Button
            appearance="secondary"
            icon={<LinkRegular />}
            disabled={!amendmentToLink || linkMutation.isPending}
            onClick={() => amendmentToLink && linkMutation.mutate(amendmentToLink)}
          >
            Link
          </Button>
        </div>

        <Divider style={{ margin: '20px 0' }} />

        {/* ── Move it along ────────────────────────────────────────────────────────────────── */}
        <h4 style={{ margin: '0 0 8px' }}>Update</h4>

        <Field label="Owner" style={{ marginBottom: '12px' }}>
          <Dropdown
            placeholder="Unassigned"
            value={notice.assignedToName ?? ''}
            onOptionSelect={(_, data) => {
              const user = systemUsers?.find((u) => u.systemUserId === data.optionValue);
              if (!user) return;
              // Both id and name are sent: the name is what the queue renders without a second
              // lookup, and G6 requires the actor on the change to be attributed.
              updateMutation.mutate({
                assignedTo: user.systemUserId,
                assignedToName: user.fullName,
              });
            }}
          >
            {(systemUsers ?? [])
              .filter((u) => !u.isDisabled)
              .map((u) => (
                <Option key={u.systemUserId} text={u.fullName} value={u.systemUserId}>
                  {u.fullName}
                </Option>
              ))}
          </Dropdown>
        </Field>

        <Field label="Status" style={{ marginBottom: '12px' }}>
          <Dropdown
            value={
              pendingStatus
                ? irsNoticeStatusDisplayNames[pendingStatus]
                : irsNoticeStatusDisplayNames[notice.status]
            }
            onOptionSelect={(_, data) =>
              setPendingStatus(data.optionValue as IrsNoticeStatus | undefined)
            }
          >
            {irsNoticeStatusOptions
              // Resolved is reached through the guarded action below, not through this dropdown, so
              // there is exactly one path to it and exactly one place the guard has to hold.
              .filter((opt) => opt.value !== 'resolved')
              .map((opt) => (
                <Option key={opt.value} text={opt.label} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
          </Dropdown>
        </Field>

        {pendingStatus && pendingStatus !== notice.status && (
          <>
            <Field label="Note (optional)" style={{ marginBottom: '8px' }}>
              <Textarea
                value={statusNotes}
                onChange={(_, data) => setStatusNotes(data.value)}
                rows={2}
                placeholder="Why is it moving?"
              />
            </Field>
            <Button
              appearance="primary"
              disabled={updateMutation.isPending}
              onClick={() =>
                updateMutation.mutate({
                  status: pendingStatus,
                  statusNotes: statusNotes.trim() || undefined,
                })
              }
              style={{ marginBottom: '20px' }}
            >
              {updateMutation.isPending
                ? 'Saving...'
                : `Move to ${irsNoticeStatusDisplayNames[pendingStatus]}`}
            </Button>
          </>
        )}

        <Divider style={{ margin: '20px 0' }} />

        {/* ── Resolve, guarded by G5, with the reason shown ─────────────────────────────────── */}
        <h4 style={{ margin: '0 0 8px' }}>Resolve</h4>

        {alreadyResolved ? (
          <MessageBar intent="success">
            <MessageBarBody>
              Resolved
              {/* Local, not UTC: resolvedAt is a real timestamp (when a person did something), and
                  "what day did we resolve it" is a wall-clock question for the reader. The received
                  and due dates above are date-only fields and render in UTC. The rule is in
                  formatNoticeDate; the two are different kinds of value, not an inconsistency. */}
              {notice.resolvedAt && ` on ${format(new Date(notice.resolvedAt), 'MMM d, yyyy')}`}
              {notice.resolvedByName && ` by ${notice.resolvedByName}`}.
              {notice.resolutionNotes ? ` ${notice.resolutionNotes}` : ''}
            </MessageBarBody>
          </MessageBar>
        ) : (
          <>
            {blockReason && (
              // The reason, always visible when the action is blocked. This is the point: a bare
              // disabled button would tell the staff member nothing about what has to happen first.
              <MessageBar intent="warning" style={{ marginBottom: '12px' }}>
                <MessageBarBody>{blockReason}</MessageBarBody>
              </MessageBar>
            )}
            <Field label="Resolution notes" style={{ marginBottom: '8px' }}>
              <Textarea
                value={resolutionNotes}
                onChange={(_, data) => setResolutionNotes(data.value)}
                rows={3}
                placeholder="What settled it?"
                disabled={!!blockReason}
              />
            </Field>
            <Button
              appearance="primary"
              icon={<CheckmarkCircleRegular />}
              disabled={!!blockReason || updateMutation.isPending}
              onClick={() =>
                updateMutation.mutate({
                  status: 'resolved',
                  resolutionNotes: resolutionNotes.trim() || undefined,
                })
              }
            >
              {updateMutation.isPending ? 'Resolving...' : 'Mark resolved'}
            </Button>
          </>
        )}

        <Divider style={{ margin: '20px 0' }} />

        {/* ── History ──────────────────────────────────────────────────────────────────────── */}
        <h4 style={{ margin: '0 0 8px' }}>History</h4>
        {notice.statusHistory.length === 0 ? (
          <div style={{ color: 'var(--tl-text-secondary)' }}>No status changes recorded yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Newest first for reading; the stored history is append-only, so this does not mutate
                it — `slice()` copies before reversing. */}
            {notice.statusHistory
              .slice()
              .reverse()
              .map((change, i) => (
                <div key={`${change.changedAt}-${i}`} style={{ fontSize: '13px' }}>
                  <div>
                    <strong>
                      {irsNoticeStatusDisplayNames[change.toStatus as IrsNoticeStatus] ??
                        change.toStatus}
                    </strong>
                    {change.changedByName && ` — ${change.changedByName}`}
                  </div>
                  <div style={{ color: 'var(--tl-text-secondary)' }}>
                    {format(new Date(change.changedAt), 'MMM d, yyyy h:mm a')}
                  </div>
                  {change.notes && <div style={{ marginTop: '2px' }}>{change.notes}</div>}
                </div>
              ))}
          </div>
        )}
      </>
    );
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={() => onOpenChange(false)}
            />
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>
              {notice ? `${notice.noticeNumber || 'Notice'} · ${notice.taxYear}` : 'Notice'}
            </span>
            {detail && (
              <Badge
                appearance="filled"
                style={{ backgroundColor: detail.statusColor, color: 'white' }}
              >
                {/* Server-owned vocabulary, so the server ships the label and the colour and the
                    frontend does not keep a second copy of them. */}
                {detail.statusDisplayName}
              </Badge>
            )}
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>{renderContent()}</DrawerBody>
    </Drawer>
  );
}
